import { useState, useEffect, useCallback, useRef } from 'react';
import salesforceEntitiesService from 'services/salesforceEntitiesService';

// A shared constant, not a `= {}` default: a fresh object literal per render would
// change the fetch callback's identity every time and re-trigger the effect forever.
const NO_FILTERS = {};

const useFundraisingStageSummary = (entityId, filters = NO_FILTERS) => {
	const [stageSummary, setStageSummary] = useState(null);
	const [totalCommitment, setTotalCommitment] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	// Tracks the entity whose summary is already on screen, so a filter change refreshes
	// the numbers in place instead of flashing the panel's skeleton again
	const loadedEntityIdRef = useRef(null);

	const fetchStageSummary = useCallback(async () => {
		if (!entityId) return;

		const isInitialLoad = loadedEntityIdRef.current !== entityId;

		if (isInitialLoad) setLoading(true);
		setError(null);

		try {
			const data = await salesforceEntitiesService.getFundraisingStageSummary(
				entityId,
				filters
			);
			setStageSummary(data.stageSummary);
			setTotalCommitment(data.totalCommitment);
			loadedEntityIdRef.current = entityId;
		} catch (err) {
			// authedApiFetch embeds the HTTP status in err.message; map the codes the
			// backend uses onto the same keys ActiveRaiseContent renders for raise metrics.
			if (err.message?.includes('404')) {
				setError('not_connected');
			} else if (err.message?.includes('403')) {
				setError('access_denied');
			} else {
				setError('unavailable');
			}
			setStageSummary(null);
			setTotalCommitment(null);
			// A failed load leaves nothing on screen, so the next attempt is an initial load again
			loadedEntityIdRef.current = null;
		} finally {
			if (isInitialLoad) setLoading(false);
		}
		// filters is the same state object useFundraisings takes, so its identity
		// only changes when the user actually changes a filter
	}, [entityId, filters]);

	useEffect(() => {
		fetchStageSummary();
	}, [fetchStageSummary]);

	return {
		stageSummary,
		totalCommitment,
		loading,
		error,
		refetch: fetchStageSummary,
	};
};

export default useFundraisingStageSummary;
